import { AnalyticsService } from '@alison/analytics/client';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { ReactNode } from 'react';
import type { CreativeDetailTab } from '../../../lib/creative-detail-search.js';
import type { DetailCreative } from '../data/api.js';
import type { CreativeDetailScope } from './data/api.js';
import { OverviewTab } from './overview/OverviewTab.js';
import { ScoresTab } from './scores/ScoresTab.js';
import { DETAIL_GUTTER } from './shared/SectionHeader.js';
import { TagsTab } from './tags/TagsTab.js';

const TAB_LABELS: Record<CreativeDetailTab, string> = {
  overview: 'Overview',
  scores: 'Scores',
  tags: 'Tags',
};

interface CreativeDetailTabsProps {
  tab: CreativeDetailTab;
  onTabChange: (tab: CreativeDetailTab) => void;
  scope: CreativeDetailScope;
  creative: DetailCreative;
  kpis: string[];
  leadingKpiLabel: string;
}

export function CreativeDetailTabs({
  tab,
  onTabChange,
  scope,
  creative,
  kpis,
  leadingKpiLabel,
}: CreativeDetailTabsProps): ReactNode {
  const { creativeId } = creative;
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={tab}
        onChange={(_event, next: CreativeDetailTab) => {
          onTabChange(next);
          AnalyticsService.track('Creative Detail - Change Tab', { tab: next });
        }}
        sx={{ px: DETAIL_GUTTER, mt: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {Object.entries(TAB_LABELS).map(([value, label]) => (
          <Tab key={value} value={value} label={label} />
        ))}
      </Tabs>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 5, px: DETAIL_GUTTER }}>
        {tab === 'overview' ? (
          <OverviewTab
            scope={scope}
            creative={creative}
            kpis={kpis}
            leadingKpiLabel={leadingKpiLabel}
            onSeeBreakdown={() => onTabChange('scores')}
          />
        ) : tab === 'scores' ? (
          <ScoresTab scope={scope} creativeId={creativeId} leadingKpiLabel={leadingKpiLabel} />
        ) : (
          <TagsTab scope={scope} creativeId={creativeId} />
        )}
      </Box>
    </Box>
  );
}
